/**
 * Reader for .NET Remoting Binary Format (MS-NRBF), the stream produced by
 * `System.Runtime.Serialization.Formatters.Binary.BinaryFormatter`.
 *
 * Spec: https://learn.microsoft.com/openspecs/windows_protocols/ms-nrbf
 *
 * The reader is generic: it does not know anything about Make It. It turns the
 * stream into a plain object graph where every class instance becomes
 * `{ $type: "ClassName", $library: "Assembly, Version=…", ...members }`,
 * arrays become JS arrays and primitives become JS primitives. Object
 * references (the same instance appearing twice) are resolved to the same JS
 * object, so cycles are preserved.
 *
 * Only the subset of the format that `BinaryFormatter` emits for ordinary
 * `[Serializable]` classes is supported: no remoting method calls.
 */
// ---------------------------------------------------------------------------
// Enums from the spec
// ---------------------------------------------------------------------------
export var RecordType;
(function (RecordType) {
    RecordType[RecordType["SerializedStreamHeader"] = 0] = "SerializedStreamHeader";
    RecordType[RecordType["ClassWithId"] = 1] = "ClassWithId";
    RecordType[RecordType["SystemClassWithMembers"] = 2] = "SystemClassWithMembers";
    RecordType[RecordType["ClassWithMembers"] = 3] = "ClassWithMembers";
    RecordType[RecordType["SystemClassWithMembersAndTypes"] = 4] = "SystemClassWithMembersAndTypes";
    RecordType[RecordType["ClassWithMembersAndTypes"] = 5] = "ClassWithMembersAndTypes";
    RecordType[RecordType["BinaryObjectString"] = 6] = "BinaryObjectString";
    RecordType[RecordType["BinaryArray"] = 7] = "BinaryArray";
    RecordType[RecordType["MemberPrimitiveTyped"] = 8] = "MemberPrimitiveTyped";
    RecordType[RecordType["MemberReference"] = 9] = "MemberReference";
    RecordType[RecordType["ObjectNull"] = 10] = "ObjectNull";
    RecordType[RecordType["MessageEnd"] = 11] = "MessageEnd";
    RecordType[RecordType["BinaryLibrary"] = 12] = "BinaryLibrary";
    RecordType[RecordType["ObjectNullMultiple256"] = 13] = "ObjectNullMultiple256";
    RecordType[RecordType["ObjectNullMultiple"] = 14] = "ObjectNullMultiple";
    RecordType[RecordType["ArraySinglePrimitive"] = 15] = "ArraySinglePrimitive";
    RecordType[RecordType["ArraySingleObject"] = 16] = "ArraySingleObject";
    RecordType[RecordType["ArraySingleString"] = 17] = "ArraySingleString";
    RecordType[RecordType["MethodCall"] = 21] = "MethodCall";
    RecordType[RecordType["MethodReturn"] = 22] = "MethodReturn";
})(RecordType || (RecordType = {}));
export var BinaryType;
(function (BinaryType) {
    BinaryType[BinaryType["Primitive"] = 0] = "Primitive";
    BinaryType[BinaryType["String"] = 1] = "String";
    BinaryType[BinaryType["Object"] = 2] = "Object";
    BinaryType[BinaryType["SystemClass"] = 3] = "SystemClass";
    BinaryType[BinaryType["Class"] = 4] = "Class";
    BinaryType[BinaryType["ObjectArray"] = 5] = "ObjectArray";
    BinaryType[BinaryType["StringArray"] = 6] = "StringArray";
    BinaryType[BinaryType["PrimitiveArray"] = 7] = "PrimitiveArray";
})(BinaryType || (BinaryType = {}));
export var PrimitiveType;
(function (PrimitiveType) {
    PrimitiveType[PrimitiveType["Boolean"] = 1] = "Boolean";
    PrimitiveType[PrimitiveType["Byte"] = 2] = "Byte";
    PrimitiveType[PrimitiveType["Char"] = 3] = "Char";
    // 4 is unused in the spec
    PrimitiveType[PrimitiveType["Decimal"] = 5] = "Decimal";
    PrimitiveType[PrimitiveType["Double"] = 6] = "Double";
    PrimitiveType[PrimitiveType["Int16"] = 7] = "Int16";
    PrimitiveType[PrimitiveType["Int32"] = 8] = "Int32";
    PrimitiveType[PrimitiveType["Int64"] = 9] = "Int64";
    PrimitiveType[PrimitiveType["SByte"] = 10] = "SByte";
    PrimitiveType[PrimitiveType["Single"] = 11] = "Single";
    PrimitiveType[PrimitiveType["TimeSpan"] = 12] = "TimeSpan";
    PrimitiveType[PrimitiveType["DateTime"] = 13] = "DateTime";
    PrimitiveType[PrimitiveType["UInt16"] = 14] = "UInt16";
    PrimitiveType[PrimitiveType["UInt32"] = 15] = "UInt32";
    PrimitiveType[PrimitiveType["UInt64"] = 16] = "UInt64";
    PrimitiveType[PrimitiveType["Null"] = 17] = "Null";
    PrimitiveType[PrimitiveType["String"] = 18] = "String";
})(PrimitiveType || (PrimitiveType = {}));
export var BinaryArrayType;
(function (BinaryArrayType) {
    BinaryArrayType[BinaryArrayType["Single"] = 0] = "Single";
    BinaryArrayType[BinaryArrayType["Jagged"] = 1] = "Jagged";
    BinaryArrayType[BinaryArrayType["Rectangular"] = 2] = "Rectangular";
    BinaryArrayType[BinaryArrayType["SingleOffset"] = 3] = "SingleOffset";
    BinaryArrayType[BinaryArrayType["JaggedOffset"] = 4] = "JaggedOffset";
    BinaryArrayType[BinaryArrayType["RectangularOffset"] = 5] = "RectangularOffset";
})(BinaryArrayType || (BinaryArrayType = {}));
export function isNrbfObject(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v) && "$type" in v;
}
/** Placeholder for a forward reference, resolved after the whole stream is read. */
class Ref {
    id;
    constructor(id) {
        this.id = id;
    }
}
class NrbfError extends Error {
    offset;
    constructor(message, offset) {
        super(`${message} (at byte offset ${offset})`);
        this.offset = offset;
        this.name = "NrbfError";
    }
}
// ---------------------------------------------------------------------------
// Binary cursor
// ---------------------------------------------------------------------------
class Cursor {
    view;
    bytes;
    pos = 0;
    utf8 = new TextDecoder("utf-8");
    constructor(data) {
        this.bytes = data;
        this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    }
    get eof() {
        return this.pos >= this.bytes.length;
    }
    u8() {
        this.check(1);
        return this.bytes[this.pos++];
    }
    i8() {
        this.check(1);
        return this.view.getInt8(this.pos++);
    }
    i16() {
        this.check(2);
        const v = this.view.getInt16(this.pos, true);
        this.pos += 2;
        return v;
    }
    u16() {
        this.check(2);
        const v = this.view.getUint16(this.pos, true);
        this.pos += 2;
        return v;
    }
    i32() {
        this.check(4);
        const v = this.view.getInt32(this.pos, true);
        this.pos += 4;
        return v;
    }
    u32() {
        this.check(4);
        const v = this.view.getUint32(this.pos, true);
        this.pos += 4;
        return v;
    }
    i64() {
        this.check(8);
        const v = this.view.getBigInt64(this.pos, true);
        this.pos += 8;
        return v;
    }
    u64() {
        this.check(8);
        const v = this.view.getBigUint64(this.pos, true);
        this.pos += 8;
        return v;
    }
    f32() {
        this.check(4);
        const v = this.view.getFloat32(this.pos, true);
        this.pos += 4;
        return v;
    }
    f64() {
        this.check(8);
        const v = this.view.getFloat64(this.pos, true);
        this.pos += 8;
        return v;
    }
    /** 7-bit encoded int32 length followed by UTF-8 bytes (LengthPrefixedString). */
    string() {
        let length = 0;
        let shift = 0;
        for (let i = 0; i < 5; i++) {
            const b = this.u8();
            length |= (b & 0x7f) << shift;
            if ((b & 0x80) === 0)
                break;
            shift += 7;
        }
        this.check(length);
        const s = this.utf8.decode(this.bytes.subarray(this.pos, this.pos + length));
        this.pos += length;
        return s;
    }
    /** A UTF-8 encoded char (1–4 bytes). */
    char() {
        this.check(1);
        const b0 = this.bytes[this.pos];
        const len = b0 < 0x80 ? 1 : b0 < 0xe0 ? 2 : b0 < 0xf0 ? 3 : 4;
        this.check(len);
        const s = this.utf8.decode(this.bytes.subarray(this.pos, this.pos + len));
        this.pos += len;
        return s;
    }
    check(n) {
        if (this.pos + n > this.bytes.length) {
            throw new NrbfError(`Unexpected end of stream, needed ${n} more byte(s)`, this.pos);
        }
    }
}
export function readNrbf(data, options = {}) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const reader = new Reader(new Cursor(bytes), options);
    return reader.read();
}
class Reader {
    c;
    objects = new Map();
    libraries = new Map();
    classes = new Map(); // keyed by objectId of the defining record
    rootId = 0;
    bigintToNumber;
    constructor(c, options) {
        this.c = c;
        this.bigintToNumber = options.bigintToNumber ?? true;
    }
    read() {
        // Header
        const type = this.c.u8();
        if (type !== RecordType.SerializedStreamHeader) {
            throw new NrbfError(`Expected SerializedStreamHeader (0), got ${type}`, this.c.pos - 1);
        }
        this.rootId = this.c.i32();
        this.c.i32(); // headerId
        const major = this.c.i32();
        const minor = this.c.i32();
        if (major !== 1 || minor !== 0) {
            throw new NrbfError(`Unsupported NRBF version ${major}.${minor}`, this.c.pos);
        }
        // Records until MessageEnd
        for (;;) {
            if (this.c.eof)
                throw new NrbfError("Stream ended without MessageEnd", this.c.pos);
            const result = this.readRecord();
            if (result === END)
                break;
        }
        const root = this.resolveAll();
        return { root, objects: this.objects, libraries: this.libraries };
    }
    // -- record dispatch ------------------------------------------------------
    /**
     * Reads one record and returns its value. Values that are objects are also
     * registered in `objects` under their id. Returns END for MessageEnd.
     */
    readRecord() {
        const start = this.c.pos;
        const type = this.c.u8();
        switch (type) {
            case RecordType.MessageEnd:
                return END;
            case RecordType.BinaryLibrary: {
                const id = this.c.i32();
                this.libraries.set(id, this.c.string());
                return this.readRecord(); // a library record is always followed by the record using it
            }
            case RecordType.ClassWithMembersAndTypes:
                return this.readClassWithMembersAndTypes(false);
            case RecordType.SystemClassWithMembersAndTypes:
                return this.readClassWithMembersAndTypes(true);
            case RecordType.ClassWithMembers:
                return this.readClassWithMembers(false);
            case RecordType.SystemClassWithMembers:
                return this.readClassWithMembers(true);
            case RecordType.ClassWithId:
                return this.readClassWithId();
            case RecordType.BinaryObjectString: {
                const id = this.c.i32();
                const s = this.c.string();
                this.objects.set(id, s);
                return s;
            }
            case RecordType.MemberPrimitiveTyped: {
                const pt = this.c.u8();
                return this.readPrimitive(pt);
            }
            case RecordType.MemberReference:
                return new Ref(this.c.i32());
            case RecordType.ObjectNull:
                return null;
            case RecordType.ObjectNullMultiple256:
                return new NullRun(this.c.u8());
            case RecordType.ObjectNullMultiple:
                return new NullRun(this.c.i32());
            case RecordType.ArraySinglePrimitive:
                return this.readArraySinglePrimitive();
            case RecordType.ArraySingleObject:
                return this.readArraySingleObject();
            case RecordType.ArraySingleString:
                return this.readArraySingleString();
            case RecordType.BinaryArray:
                return this.readBinaryArray();
            case RecordType.MethodCall:
            case RecordType.MethodReturn:
                throw new NrbfError("Remoting method records are not supported", start);
            default:
                throw new NrbfError(`Unknown record type ${type}`, start);
        }
    }
    // -- class records --------------------------------------------------------
    readClassInfo() {
        const objectId = this.c.i32();
        const name = this.c.string();
        const count = this.c.i32();
        const memberNames = [];
        for (let i = 0; i < count; i++)
            memberNames.push(this.c.string());
        return { objectId, name, memberNames };
    }
    readMemberTypeInfo(count) {
        const types = [];
        for (let i = 0; i < count; i++)
            types.push({ binaryType: this.c.u8() });
        for (const t of types) {
            switch (t.binaryType) {
                case BinaryType.Primitive:
                case BinaryType.PrimitiveArray:
                    t.extra = this.c.u8();
                    break;
                case BinaryType.SystemClass:
                    t.extra = this.c.string();
                    break;
                case BinaryType.Class:
                    t.extra = this.c.string();
                    t.libraryId = this.c.i32();
                    break;
                default:
                    break; // String, Object, ObjectArray, StringArray carry no extra info
            }
        }
        return types;
    }
    readClassWithMembersAndTypes(system) {
        const info = this.readClassInfo();
        const memberTypes = this.readMemberTypeInfo(info.memberNames.length);
        const libraryId = system ? -1 : this.c.i32();
        const cls = { ...info, memberTypes, libraryId };
        this.classes.set(info.objectId, cls);
        return this.readClassBody(cls, info.objectId);
    }
    readClassWithMembers(system) {
        const info = this.readClassInfo();
        const libraryId = system ? -1 : this.c.i32();
        const cls = { ...info, memberTypes: null, libraryId };
        this.classes.set(info.objectId, cls);
        return this.readClassBody(cls, info.objectId);
    }
    readClassWithId() {
        const objectId = this.c.i32();
        const metadataId = this.c.i32();
        const cls = this.classes.get(metadataId);
        if (!cls)
            throw new NrbfError(`ClassWithId refers to unknown metadata id ${metadataId}`, this.c.pos - 4);
        return this.readClassBody(cls, objectId);
    }
    readClassBody(cls, objectId) {
        const obj = {
            $type: cls.name,
            $library: cls.libraryId === -1 ? "mscorlib" : this.libraries.get(cls.libraryId) ?? `library#${cls.libraryId}`,
            $id: objectId,
        };
        // Register before reading members so self-references resolve.
        this.objects.set(objectId, obj);
        for (let i = 0; i < cls.memberNames.length; i++) {
            const name = cls.memberNames[i];
            const t = cls.memberTypes ? cls.memberTypes[i] : null;
            obj[name] = this.readMemberValue(t);
        }
        return obj;
    }
    /** Reads the value of a member given its declared type (or null when untyped). */
    readMemberValue(t) {
        if (t && t.binaryType === BinaryType.Primitive) {
            return this.readPrimitive(t.extra);
        }
        // Everything else is a record: string, null, reference, nested class, typed primitive…
        const v = this.readRecord();
        if (v === END)
            throw new NrbfError("Unexpected MessageEnd inside a class", this.c.pos);
        if (v instanceof NullRun) {
            // A null run is only legal inside arrays; tolerate a run of 1 as null.
            if (v.count === 1)
                return null;
            throw new NrbfError("ObjectNullMultiple found outside an array", this.c.pos);
        }
        return v;
    }
    // -- arrays ---------------------------------------------------------------
    readArraySinglePrimitive() {
        const id = this.c.i32();
        const length = this.c.i32();
        const pt = this.c.u8();
        const arr = new Array(length);
        for (let i = 0; i < length; i++)
            arr[i] = this.readPrimitive(pt);
        this.objects.set(id, arr);
        return arr;
    }
    readArraySingleObject() {
        const id = this.c.i32();
        const length = this.c.i32();
        const arr = this.readArrayItems(length);
        this.objects.set(id, arr);
        return arr;
    }
    readArraySingleString() {
        const id = this.c.i32();
        const length = this.c.i32();
        const arr = this.readArrayItems(length);
        this.objects.set(id, arr);
        return arr;
    }
    readBinaryArray() {
        const id = this.c.i32();
        const arrayType = this.c.u8();
        const rank = this.c.i32();
        const lengths = [];
        for (let i = 0; i < rank; i++)
            lengths.push(this.c.i32());
        if (arrayType === BinaryArrayType.SingleOffset ||
            arrayType === BinaryArrayType.JaggedOffset ||
            arrayType === BinaryArrayType.RectangularOffset) {
            for (let i = 0; i < rank; i++)
                this.c.i32(); // lower bounds, ignored
        }
        const [itemType] = this.readMemberTypeInfo(1);
        const total = lengths.reduce((a, b) => a * b, 1);
        let flat;
        if (itemType.binaryType === BinaryType.Primitive) {
            flat = new Array(total);
            for (let i = 0; i < total; i++)
                flat[i] = this.readPrimitive(itemType.extra);
        }
        else {
            flat = this.readArrayItems(total);
        }
        const arr = rank <= 1 ? flat : reshape(flat, lengths);
        this.objects.set(id, arr);
        return arr;
    }
    /** Reads `length` array items, each a record (handles null runs). */
    readArrayItems(length) {
        const arr = new Array(length);
        let i = 0;
        while (i < length) {
            const v = this.readRecord();
            if (v === END)
                throw new NrbfError("Unexpected MessageEnd inside an array", this.c.pos);
            if (v instanceof NullRun) {
                for (let k = 0; k < v.count && i < length; k++)
                    arr[i++] = null;
            }
            else {
                arr[i++] = v; // may be a Ref, resolved later
            }
        }
        return arr;
    }
    // -- primitives -----------------------------------------------------------
    readPrimitive(pt) {
        switch (pt) {
            case PrimitiveType.Boolean:
                return this.c.u8() !== 0;
            case PrimitiveType.Byte:
                return this.c.u8();
            case PrimitiveType.SByte:
                return this.c.i8();
            case PrimitiveType.Char:
                return this.c.char();
            case PrimitiveType.Decimal:
                return Number(this.c.string());
            case PrimitiveType.Double:
                return this.c.f64();
            case PrimitiveType.Single:
                return this.c.f32();
            case PrimitiveType.Int16:
                return this.c.i16();
            case PrimitiveType.UInt16:
                return this.c.u16();
            case PrimitiveType.Int32:
                return this.c.i32();
            case PrimitiveType.UInt32:
                return this.c.u32();
            case PrimitiveType.Int64:
                return this.big(this.c.i64());
            case PrimitiveType.UInt64:
                return this.big(this.c.u64());
            case PrimitiveType.TimeSpan:
                return this.big(this.c.i64()); // ticks
            case PrimitiveType.DateTime:
                return this.big(this.c.i64()); // ticks + kind bits
            case PrimitiveType.String:
                return this.c.string();
            case PrimitiveType.Null:
                return null;
            default:
                throw new NrbfError(`Unknown primitive type ${pt}`, this.c.pos);
        }
    }
    big(v) {
        if (!this.bigintToNumber)
            return v;
        if (v >= BigInt(Number.MIN_SAFE_INTEGER) && v <= BigInt(Number.MAX_SAFE_INTEGER))
            return Number(v);
        return v;
    }
    // -- reference resolution -------------------------------------------------
    resolveAll() {
        const resolve = (v) => {
            if (v instanceof Ref) {
                if (!this.objects.has(v.id))
                    throw new NrbfError(`Dangling reference to object ${v.id}`, this.c.pos);
                return this.objects.get(v.id);
            }
            return v;
        };
        for (const value of this.objects.values()) {
            if (Array.isArray(value)) {
                for (let i = 0; i < value.length; i++)
                    value[i] = resolve(value[i]);
            }
            else if (isNrbfObject(value)) {
                for (const key of Object.keys(value)) {
                    if (key.startsWith("$"))
                        continue;
                    value[key] = resolve(value[key]);
                }
            }
        }
        const root = this.objects.get(this.rootId);
        if (root === undefined)
            throw new NrbfError(`Root object ${this.rootId} not found`, this.c.pos);
        return root;
    }
}
const END = Symbol("MessageEnd");
class NullRun {
    count;
    constructor(count) {
        this.count = count;
    }
}
function reshape(flat, lengths) {
    if (lengths.length === 1)
        return flat;
    const [first, ...rest] = lengths;
    const chunk = rest.reduce((a, b) => a * b, 1);
    const out = [];
    for (let i = 0; i < first; i++)
        out.push(reshape(flat.slice(i * chunk, (i + 1) * chunk), rest));
    return out;
}
//# sourceMappingURL=reader.js.map